import MuxPlayer from '@mux/mux-player-react';
import { fieldStateCss } from '@src/components/common/fieldStateStyles';
import SkeletonBox from '@src/components/common/SkeletonBox';
import SpeakerIdentificationPrompt from '@src/components/sidepanel/modals/SpeakerIdentificationPrompt';
import { useLocalPreviewVideoUrl } from '@src/hooks/useLocalPreviewVideoUrl';
import useVideoCreatorDataInitialization from '@src/hooks/useVideoCreatorDataInitialization';
import { ClipPreviewStore } from '@src/stores-v2/ClipPreviewStore';
import {
  useClipPreview,
  useStore,
  useTemplatedPrompts,
  useUserIdentity,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import SimplePencilIcon from '@src/svgs/SimplePencilIcon';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import {
  stabilizeStoryMetadata,
  type StableStoryMetadata,
} from '@src/utility/stableStoryMetadata';
import { parseClipThemeTitles } from '@src/utility/story';
import { isCompressedRenditionPending } from '@src/utility/storyReadiness';
import { forIPad } from '@src/utility/styled';
import { observer } from 'mobx-react-lite';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef as useReactRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import styled, { css } from 'styled-components';

import { ContentTab } from '../types.ts/general';
import { getPosterUrl } from '../utility/general';
import { useClickAway } from '../utility/useClickAway';
import { useFlagsCombination } from '../utility/useFlagsCombination';
import useRequestState from '../utility/useRequestState';
import { handleVideoPlayerError } from '../utility/videoPlayerErrorHandler';
import Badge from './common/Badge';
import Modal from './common/Modal';
import PreviewProcessingOverlay from './common/PreviewProcessingOverlay';
import ReplacePhotoElementModal from './common/ReplacePhotoElementModal';
import SavedTag from './common/SavedTag';
import { PARAGRAPH_TOOLTIP_STYLE } from './common/tooltipStyles';
import ContentStudioSkeleton from './content-components/ContentStudioSkeleton';
import ContentView from './content-components/ContentView';
import EditVideoButton from './content-components/EditVideoButton';
import { PrewarmedRendererProvider } from './content-components/PrewarmedRendererContext';
import QuerySubscriptionComponent from './content-components/QuerySubscriptionComponent';
import SocialShareStatus from './content-components/SocialShareStatus';
import SocialStats from './content-components/SocialStats';
import {
  CONTAINED_TABS,
  parseContentTab,
  type ScrollMode,
} from './content-components/tabs';
import { CompositionProvider } from './sidepanel/CompositionContext';

import type { AiGeneratedContent, Story, Video } from '../types.ts/story';
import type { FC } from 'react';
import type { CSSProperties } from 'styled-components';

type Props = {
  params: {
    [k: string]: string;
  };
};

const logger = LogManager.getLogger('feature:ContentStudio');

const playerStyle = {
  height: '259px',
  minHeight: '259px',
  width: '100%',
  minWidth: '350px',
  maxWidth: '460px',
  borderRadius: 10,
  overflow: 'hidden',
  border: '1px solid var(--border)',
  aspectRatio: 16 / 9,
  '--pip-button': 'none',
  '--center-controls': 'none',
} as CSSProperties;

// Video player context is now moved to TranscriptEditorWrapper.tsx
// Transcript editor context is defined in TranscriptEditorContext.tsx

const ContentStudio: FC<Props> = observer(() => {
  const videoCreator = useVideoCreatorStore();
  const userIdentity = useUserIdentity();
  const { datoClientStore, analyticsStore } = useStore();
  const templatedPromptsStore = useTemplatedPrompts();
  const clipPreviewStore = useClipPreview();

  // Clear clip and video asset caches when leaving Content Studio.
  useEffect(() => {
    return () => {
      clipPreviewStore.clearClipResourceCache();
    };
  }, [clipPreviewStore]);

  // Pre-warm the VideoTemplateRepository cache so that when a clip modal opens,
  // the template fetch in VideoTemplatesContext is an instant cache hit.
  useEffect(() => {
    const templateIds = templatedPromptsStore.videoTemplatesIds;
    if (!videoCreator.organization || !templateIds?.length) return;

    try {
      datoClientStore.videoTemplateRepository
        .getVideoTemplates(templateIds, {
          excludeGlobalPresets:
            videoCreator.organization.customerType?.excludeFromGlobalTemplate ??
            false,
        })
        .catch(() => {
          // Silent — pre-warming is best-effort
        });
    } catch {
      // videoTemplateRepository getter throws if not yet initialized — ignore
    }
  }, [videoCreator.organization, templatedPromptsStore.videoTemplatesIds]);

  const story = videoCreator?.story;
  // PLAT-11228: the session's locally-held upload, playable before renditions.
  const localPreviewUrl = useLocalPreviewVideoUrl(story?.id);
  const renditionPending = isCompressedRenditionPending(story);

  // Inline story name editing
  const [isEditingStoryName, setIsEditingStoryName] = useState(false);
  const [editingStoryName, setEditingStoryName] = useState('');
  const storyNameInputRef = useReactRef<HTMLInputElement>(null);

  // Track TopContent height so descendant sticky elements (Tabs in ContentView)
  // can offset themselves below it via the --content-studio-header-h CSS var.
  const mainRef = useReactRef<HTMLDivElement>(null);
  const topContentRef = useReactRef<HTMLDivElement>(null);

  useEffect(() => {
    const headerEl = topContentRef.current;
    const mainEl = mainRef.current;
    if (!headerEl || !mainEl) return;
    let lastValue = '';
    let rafId = 0;
    const update = () => {
      // Defer the style write to the next frame to avoid the
      // "ResizeObserver loop completed with undelivered notifications"
      // warning, which fires when the callback synchronously mutates
      // layout-affecting state. Skip no-op writes too.
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const next = `${headerEl.offsetHeight}px`;
        if (next === lastValue) return;
        lastValue = next;
        mainEl.style.setProperty('--content-studio-header-h', next);
      });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(headerEl);
    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, []);

  const handleStartEditStoryName = useCallback(() => {
    if (!story) return;
    setEditingStoryName(story.title || '');
    setIsEditingStoryName(true);
    setTimeout(() => storyNameInputRef.current?.select(), 0);
  }, [story]);

  const handleSaveStoryName = useCallback(async () => {
    setIsEditingStoryName(false);
    const trimmed = editingStoryName.trim();
    if (!story?.id || !trimmed || trimmed === story.title) return;
    const previousTitle = story.title;
    story.title = trimmed;
    try {
      await videoCreator.updateStory({ id: story.id, title: trimmed });
    } catch (error) {
      story.title = previousTitle;
      logger.error('Failed to rename story', extractErrorInfo(error));
    }
  }, [editingStoryName, story, videoCreator]);

  const {
    enableSharing,
    enableGenerateAiPhoto,
    contentStudioDashboardEnableAnalytics,
    enableTranslationReviewerMode,
  } = useFlagsCombination(userIdentity.currentRole);
  const [urlSearchParams] = useSearchParams();
  const {
    storyId: storyIdFromUrlParams,
    playbackId,
    showcase,
  } = Object.fromEntries(urlSearchParams.entries()) || {};

  const storyId = storyIdFromUrlParams || story?.id;

  // Tabs whose internal layout depends on the original `height: 100vh +
  // overflow: hidden` outer container (legacy `flex:1; min-height:0`
  // chain). For these tabs we keep the pre-branch outer Main so they
  // render exactly as before — no document scroll, single inner scrollbar.
  const activeContentTab = parseContentTab(urlSearchParams.get('tab'));
  const scrollMode: ScrollMode =
    activeContentTab != null && CONTAINED_TABS.has(activeContentTab)
      ? 'contained'
      : 'page';

  // The story on screen isn't (yet) the one the URL asked for — a cold load, or
  // a switch to another story. Only ever true while a fetch is in flight: once
  // it lands, the page has its real layout and is never re-skeletoned.
  //
  // Deliberately ungated by any "wait until it's been slow" delay. During such a
  // delay the page renders its real chrome around missing data — blank title,
  // black player, and a "No clips yet" that answers a question the app can't yet
  // answer — so the skeleton has to stand in from the very first frame.
  const isStoryLoading = storyIdFromUrlParams
    ? story?.id !== storyIdFromUrlParams
    : Boolean(showcase) && !story;
  // The skeleton mirrors the Video Clips tab, which is the default and where
  // every entry point into Content Studio lands. A deep link to another tab
  // keeps that tab's own loading behavior rather than flashing a layout that
  // isn't the one about to render.
  const showSkeleton =
    isStoryLoading &&
    (activeContentTab == null || activeContentTab === ContentTab.video_clip);

  const finalVideo = story?.finalVideo as Video | undefined;
  const originalVideo = story?.originalVideo;

  const datoThumnbailUrl = finalVideo?.thumbnail?.url || story?.thumbnail?.url;
  const posterUrl = datoThumnbailUrl
    ? getPosterUrl(datoThumnbailUrl, 780, 440)
    : originalVideo?.video?.thumbnailUrl;

  let finalPlaybackId =
    playbackId ||
    finalVideo?.videoFilePrimary?.video?.muxPlaybackId ||
    originalVideo?.video?.muxPlaybackId;

  // Check if there are any analytics to display (non-zero values in shared contents)
  const hasAnalytics = useMemo(() => {
    const sharedContents = story?._allReferencingSharedContents;
    if (!sharedContents || sharedContents.length === 0) return false;
    const totalViews = sharedContents.reduce(
      (sum, sc) => sum + (sc.views || 0),
      0,
    );
    const totalLikes = sharedContents.reduce(
      (sum, sc) => sum + (sc.likes || 0),
      0,
    );
    const totalShares = sharedContents.reduce(
      (sum, sc) => sum + (sc.shares || 0),
      0,
    );
    const totalComments = sharedContents.reduce(
      (sum, sc) => sum + (sc.comments || 0),
      0,
    );
    return (
      totalViews > 0 || totalLikes > 0 || totalShares > 0 || totalComments > 0
    );
  }, [story?._allReferencingSharedContents]);

  // Compute the video URL to pre-cache in the warmer renderer.
  // All clips in Content Studio share the same story video, so pre-caching
  // it early (when the warmer boots) avoids a cold download when the first
  // clip modal opens.
  const videoCacheUrl = useMemo(() => {
    return ClipPreviewStore.getLowResVideoUrl(story) ?? null;
  }, [story?.originalVideo]);

  useVideoCreatorDataInitialization();

  useEffect(() => {
    if (storyId) {
      templatedPromptsStore.initTemplatedPrompts(storyId);
      analyticsStore.setContext(
        storyId,
        showcase || videoCreator.organization?.slug || 'Unknown',
      );
    }
  }, [storyId, showcase, videoCreator.organization?.slug]);

  useEffect(() => {
    videoCreator.setAiImageFeatureFlag(enableGenerateAiPhoto);
  }, [storyId, enableGenerateAiPhoto]);

  useEffect(() => {
    if (story?.id && videoCreator.isInitialized) {
      (async () => {
        try {
          await videoCreator.loadTranscriptIfNeeded();
        } catch (error) {
          console.error('Failed to load transcript in Content Studio:', error);
        }
      })();
    }
  }, [story?.id, videoCreator.isInitialized]);

  const savedDescriptionContent = getDescriptionContentFromStory(story);
  const incomingDescription = savedDescriptionContent?.response || '';
  const incomingStoryTags = useMemo(
    () => parseClipThemeTitles(story?.clipThemesJson),
    [story?.clipThemesJson],
  );
  const stableMetadataRef = useReactRef<StableStoryMetadata>({
    storyId: null,
    description: '',
    topics: [],
  });
  const storyMatchesRoute = !!story?.id && (!storyId || story.id === storyId);
  const stableMetadata = storyMatchesRoute
    ? stabilizeStoryMetadata(stableMetadataRef.current, {
        storyId: story.id,
        description: incomingDescription,
        topics: incomingStoryTags,
        preserveSparseSnapshots: renditionPending || !!localPreviewUrl,
      })
    : { storyId: null, description: '', topics: [] };
  useEffect(() => {
    stableMetadataRef.current = stableMetadata;
  }, [stableMetadata]);
  const stableDescription = stableMetadata.description;
  const storyTags = stableMetadata.topics;
  const [description, setDescription] = useState(stableDescription);
  const [isEditing, setIsEditing] = useState(false);
  useEffect(() => {
    if (storyMatchesRoute && !isEditing) setDescription(stableDescription);
  }, [stableDescription, storyMatchesRoute, isEditing]);
  const { isSaving, isError, isSaved, request } = useRequestState();
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [isDescriptionOverflowing, setIsDescriptionOverflowing] =
    useState(false);
  const descriptionRef = useReactRef<HTMLParagraphElement>(null);

  useEffect(() => {
    // Wait a frame so the <p> element has its final layout after remount
    const rafId = requestAnimationFrame(() => {
      const el = descriptionRef.current;
      if (el) {
        // Temporarily remove line-clamp to measure true scroll height
        const originalClamp = el.style.webkitLineClamp;
        const originalOverflow = el.style.overflow;
        el.style.webkitLineClamp = 'unset';
        el.style.overflow = 'visible';
        const fullHeight = el.scrollHeight;
        el.style.webkitLineClamp = originalClamp;
        el.style.overflow = originalOverflow;
        setIsDescriptionOverflowing(fullHeight > el.clientHeight);
      }
    });
    return () => cancelAnimationFrame(rafId);
  }, [description, isEditing]);
  const handleSave = async () => {
    if (story && description.trim() !== savedDescriptionContent?.response) {
      await request(async () => {
        if (!story.aiResponse) story.aiResponse = { responses: [] };
        const descriptionContent = getDescriptionContentFromStory(story);
        const updatedDescriptionContent: AiGeneratedContent<'Description'> = {
          ...descriptionContent,
          title: 'Description',
          response: description || '',
        };
        const descriptionContentIndex = story.aiResponse.responses.findIndex(
          i => i.title.toLowerCase() === 'description',
        );
        if (
          descriptionContentIndex !== undefined &&
          descriptionContentIndex !== -1
        ) {
          story.aiResponse.responses.splice(
            descriptionContentIndex,
            1,
            updatedDescriptionContent,
          );
        } else {
          story.aiResponse.responses.push(updatedDescriptionContent);
        }
        await datoClientStore.storyRepository!.update(story);
      });
    }
    if (stableMetadataRef.current.storyId === story?.id) {
      stableMetadataRef.current = {
        ...stableMetadataRef.current,
        description,
      };
    }
    setIsEditing(false);
  };
  const inputRef = useClickAway(handleSave);
  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
    }
  }, [isEditing]);

  async function refreshStory() {
    // update story in store, so that the new profile is added to the list
    if (storyId) {
      const story = await videoCreator.findOneStory(storyId);

      if (story) {
        videoCreator.story = story;
      }
    }
  }

  function getPreferredPreviewVideoUrl() {
    // PLAT-11224: while renditions are still being produced, never hand the
    // player the unprocessed original — absence means "not yet". Whatever
    // compressed rendition already exists is fine; otherwise the player
    // waits (processing badge below).
    if (isCompressedRenditionPending(story)) {
      return (
        originalVideo?.video?.mp4UrlMedium || originalVideo?.video?.mp4UrlLow
      );
    }
    // Prefer 720p for preview if available
    // If 720p not available, try higher resolutions (1080p, original)
    // Only fall back to lower resolutions if no high-res available
    return (
      originalVideo?.video?.mp4UrlHigh ||
      originalVideo?.video?.mp4UrlHd ||
      originalVideo?.video?.mp4Url ||
      originalVideo?.video?.mp4UrlMedium ||
      originalVideo?.video?.mp4UrlLow
    );
  }

  function isHighResNotReady() {
    // Only show low-res warning when video dimensions are >= 720p
    // but no high-res URLs (720p, 1080p, or original) are available
    if (!originalVideo) return false;

    const hasHighResUrl = !!(
      originalVideo.video?.mp4UrlHigh ||
      originalVideo.video?.mp4UrlHd ||
      originalVideo.video?.mp4Url
    );

    const originalResolution = Math.min(
      originalVideo.width,
      originalVideo.height,
    );

    return originalResolution >= 720 && !hasHighResUrl;
  }

  const handlePlayerError = (event: Event) => {
    handleVideoPlayerError(event, {
      storyId: story?.id,
      storyTitle: story?.title,
      playbackId: finalPlaybackId,
      videoSource: finalPlaybackId ? 'mux' : 'direct-mp4',
      videoUrl: finalPlaybackId ? undefined : getPreferredPreviewVideoUrl(),
      posterUrl,
    });
  };

  // PLAT-11224/11228: audio-first — the story opened at transcription-ready
  // and no preview rendition exists yet. If this session still holds the
  // uploaded file, play it locally; otherwise render a placeholder instead of
  // the player (a src-less MuxPlayer shows a broken poster image and fires
  // errors).
  const renditionUrlMissing =
    !finalPlaybackId &&
    isCompressedRenditionPending(story) &&
    !getPreferredPreviewVideoUrl();
  const previewProcessing = renditionUrlMissing && !localPreviewUrl;

  const videoSection = (
    <StoryContent>
      <PlayerArea>
        {previewProcessing ? (
          <ProcessingPlayerPlaceholder style={playerStyle}>
            <PreviewProcessingOverlay compact />
          </ProcessingPlayerPlaceholder>
        ) : (
          <MuxPlayer
            style={playerStyle}
            streamType="on-demand"
            playbackId={finalPlaybackId}
            src={
              finalPlaybackId
                ? undefined
                : getPreferredPreviewVideoUrl() || localPreviewUrl || undefined
            }
            // PLAT-11228: no story thumbnail exists yet while renditions are
            // pending — a poster URL would render as a broken image.
            poster={renditionUrlMissing ? undefined : posterUrl}
            onError={handlePlayerError}
          />
        )}
        {!enableTranslationReviewerMode && <EditVideoButton />}
        {!finalPlaybackId && isHighResNotReady() && (
          <LowResStatusText>
            <Badge
              borderColor="var(--border)"
              textColor="var(--foreground)"
              data-tooltip-id="low-res-source-tooltip"
              data-tooltip-content="The high resolution version is still processing. This setting only changes the video preview. It won't affect the exported version."
            >
              <span>LOW-RES PREVIEW</span>
            </Badge>
            <Tooltip
              id="low-res-source-tooltip"
              place="top-start"
              style={PARAGRAPH_TOOLTIP_STYLE}
            />
          </LowResStatusText>
        )}
      </PlayerArea>
      <DescriptionAndAnalytics>
        {enableSharing &&
          hasAnalytics &&
          contentStudioDashboardEnableAnalytics && (
            <Analytics>
              <AnalyticsTop>
                <span>Aggregate Analytics</span>
                <SocialShareStatus
                  padding="0"
                  sharedContents={story?._allReferencingSharedContents}
                  showOnlyShared={true}
                />
              </AnalyticsTop>

              <SocialStats
                type="story"
                id={story?.id}
                allReferencingSharedContents={
                  story?._allReferencingSharedContents
                }
                layout="studio-main"
              />
            </Analytics>
          )}

        <AccordionSection open showDivider={hasAnalytics}>
          <AccordionSummary>
            <h3>Description</h3>
            <span className="accordion-actions">
              <SavedTag isSaved={isSaved} isError={isError} />
            </span>
          </AccordionSummary>
          <Description isSaving={isSaving}>
            {isEditing ? (
              <div className="description-edit-content">
                <textarea
                  ref={inputRef}
                  value={description}
                  disabled={isSaving}
                  onChange={e => setDescription(e.target.value)}
                />
              </div>
            ) : (
              <DescriptionReadView>
                <p
                  ref={descriptionRef}
                  className="description-content"
                  onClick={
                    enableTranslationReviewerMode
                      ? undefined
                      : () => setIsEditing(true)
                  }
                >
                  {description ? (
                    description
                  ) : enableTranslationReviewerMode ? null : (
                    <span>Click to add description...</span>
                  )}
                </p>
                {isDescriptionOverflowing && description && (
                  <SeeMoreButton
                    onClick={e => {
                      e.stopPropagation();
                      setShowDescriptionModal(true);
                    }}
                  >
                    See more
                  </SeeMoreButton>
                )}
              </DescriptionReadView>
            )}
          </Description>
        </AccordionSection>
        {storyTags.length > 0 && (
          <TopicsInline>
            <h3>Topics</h3>
            <TagsContainer>
              {storyTags.map((tag, idx) => (
                <Tag key={idx}>{tag}</Tag>
              ))}
            </TagsContainer>
          </TopicsInline>
        )}
      </DescriptionAndAnalytics>
      {storyTags.length > 0 && (
        <TopicsBelow>
          <h3>Topics</h3>
          <TagsContainer>
            {storyTags.map((tag, idx) => (
              <Tag key={idx}>{tag}</Tag>
            ))}
          </TagsContainer>
        </TopicsBelow>
      )}
    </StoryContent>
  );

  return (
    <Main ref={mainRef} $scrollMode={scrollMode}>
      <TopContent ref={topContentRef}>
        <TitleRow>
          {isStoryLoading ? (
            /* Exactly one title line tall, so the sticky header keeps its
               height — and the --content-studio-header-h offset the tab strip
               sticks to — when the real title arrives. On every tab, not
               just the one `showSkeleton` covers: an empty title collapses. */
            <SkeletonBox
              $width="320px"
              $height="var(--leading-page-title)"
              $radius="8px"
            />
          ) : enableTranslationReviewerMode ? (
            <Title>
              <h2>{story?.title || ''}</h2>
            </Title>
          ) : isEditingStoryName ? (
            <StoryNameInput
              ref={storyNameInputRef}
              value={editingStoryName}
              onChange={e => setEditingStoryName(e.target.value)}
              onBlur={handleSaveStoryName}
              onKeyDown={e => {
                if (e.key === 'Enter') handleSaveStoryName();
                else if (e.key === 'Escape') setIsEditingStoryName(false);
              }}
            />
          ) : (
            <TitleClickable onClick={handleStartEditStoryName}>
              <Title>
                <h2>{story?.title || ''}</h2>
              </Title>
              <StoryNameEditIcon aria-hidden>
                <SimplePencilIcon width="14" height="14" />
              </StoryNameEditIcon>
            </TitleClickable>
          )}
        </TitleRow>
      </TopContent>

      {/* The provider stays mounted across the wait: it owns the pre-warmed
          renderer iframe, which is disposed on unmount and reloads cold. */}
      <PrewarmedRendererProvider videoCacheUrl={videoCacheUrl}>
        <CompositionProvider isQuoteCard={true}>
          {showSkeleton ? (
            <ContentStudioSkeleton />
          ) : (
            <ContentView videoSection={videoSection} />
          )}
        </CompositionProvider>
      </PrewarmedRendererProvider>
      {Boolean(videoCreator.pendingSharedContentIds?.length) && (
        <QuerySubscriptionComponent
          sharedContentIds={videoCreator.pendingSharedContentIds}
          onDataReceived={async sharedContent => {
            videoCreator.showToast({
              state: 'success',
              message: 'Story successfully published',
            });
            videoCreator.pendingSharedContentIds =
              videoCreator.pendingSharedContentIds.filter(
                id => !sharedContent.find((content: any) => content.id === id),
              );
            refreshStory();
          }}
          onError={() => {
            console.error('QuerySubscriptionComponent error');
          }}
        />
      )}
      {videoCreator.openPhotoElementReplacementModal?.element && (
        <ReplacePhotoElementModal />
      )}
      <SpeakerIdentificationPrompt />
      {showDescriptionModal && (
        <Modal
          isOpen
          closeModal={() => {
            setDescription(stableDescription);
            setShowDescriptionModal(false);
          }}
          backgroundColor="var(--modal-backdrop)"
          contentStyle={{
            maxWidth: '600px',
            width: '90vw',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '12px',
            padding: '24px 28px',
          }}
        >
          <DescriptionModalHeader>
            <h3>Description</h3>
          </DescriptionModalHeader>
          {/* Reviewers reach this modal through "See more" — the only way to
              read a clamped description in full — so it stays open for them,
              but as a read-only view: no editing, no Save. */}
          <DescriptionModalTextarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            disabled={isSaving}
            readOnly={enableTranslationReviewerMode}
            placeholder="Click to add description..."
          />
          {!enableTranslationReviewerMode && (
            <DescriptionModalActions>
              <SavedTag isSaved={isSaved} isError={isError} />
              <DescriptionModalButtonGroup>
                <ModalCancelButton
                  onClick={() => {
                    setDescription(stableDescription);
                    setShowDescriptionModal(false);
                  }}
                >
                  Cancel
                </ModalCancelButton>
                <ModalSaveButton
                  disabled={
                    isSaving ||
                    description.trim() ===
                      (savedDescriptionContent?.response || '')
                  }
                  onClick={async () => {
                    await handleSave();
                    setShowDescriptionModal(false);
                  }}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </ModalSaveButton>
              </DescriptionModalButtonGroup>
            </DescriptionModalActions>
          )}
        </Modal>
      )}
    </Main>
  );
});

const getDescriptionContentFromStory = (story?: Story) => {
  const aiContent = story?.aiResponse?.responses;
  const descriptionContent = aiContent?.find(
    c => c.title.toLowerCase() === 'description',
  ) as AiGeneratedContent<'Description'> | undefined;
  return descriptionContent;
};

export default ContentStudio;

/* ------------------------------------------------------------------ */
/*  Layout styles                                                      */
/* ------------------------------------------------------------------ */

const Main = styled.div<{ $scrollMode: ScrollMode }>`
  display: flex;
  flex-direction: column;
  /* Same gutters as Story Manager's page container (PLAT-11039). The previous
     clamp(--spacing-md, 6vw, 100px) spent 77px per side at 1280 and 100px on
     large displays, which — with the app sidebar — squeezed the tab strip
     until its last tabs fell off screen. */
  padding: 0 var(--spacing-2xl);
  ${props =>
    props.$scrollMode === 'contained'
      ? css`
          /* Pre-branch behavior for tabs whose internal layout depends on a
             bounded ancestor (Talking Points, Media, Transcript Editor,
             Transcript & Speakers). */
          height: 100vh;
          overflow: hidden;
        `
      : css`
          /* Page-mode tabs (Video Clip, Pull Quotes, Articles, Social Posts,
             Email): the document scrolls naturally. */
          min-height: 100vh;
        `}
`;

const TopContent = styled.div`
  position: sticky;
  top: 0;
  flex-shrink: 0;
  z-index: 11;
  background: var(--background);
  padding-top: var(--spacing-md);
  padding-bottom: var(--spacing-sm);
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
`;

const StoryNameEditIcon = styled.span`
  display: none;
  align-items: center;
  color: var(--text-muted);
  flex-shrink: 0;
  transition: color 0.15s ease;
`;

const TitleClickable = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: 1px solid transparent;
  padding: 3px 7px;
  margin: -4px -8px;
  border-radius: var(--radius-input);
  cursor: text;
  transition: background 0.15s ease;

  &:hover {
    background: var(--sidebar-hover);

    ${StoryNameEditIcon} {
      display: flex;
    }
  }
`;

const StoryNameInput = styled.input`
  background: var(--surface);
  ${fieldStateCss}
  border-radius: var(--radius-input);
  color: var(--foreground);
  font-size: var(--text-page-title);
  font-weight: var(--font-weight-bold);
  line-height: var(--leading-page-title);
  padding: 3px 7px;
  margin: -4px -8px;
  width: calc(100% + 16px);
  outline: none;

  &::selection {
    background: var(--primary-tint);
  }
`;

const Title = styled.div`
  display: flex;
  flex-direction: column;
  h2 {
    margin: 0;
    font-size: var(--text-page-title);
    font-weight: var(--font-weight-bold);
    color: var(--foreground);
    line-height: var(--leading-page-title);
  }
`;

const Analytics = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const AnalyticsTop = styled.div`
  display: flex;
  justify-content: space-between;

  span {
    font-weight: var(--font-weight-bold);
    font-size: 20px;
    line-height: 24.2px;
    color: var(--text-secondary);
  }

  ${forIPad(`
    width: 100%;
    min-width: 500px;
    max-width: 683px;
  `)};
`;

const StoryContent = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(300px, 460px) 1fr;
  grid-template-rows: auto auto;
  column-gap: var(--spacing-lg);
  row-gap: 0;
  margin-top: 0;
  margin-bottom: 30px;

  @media only screen and (max-width: 900px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto auto;
    row-gap: 20px;
  }

  ${forIPad(`
    mux-player::part(control-bar bottom),
    mux-player::part(bottom time range) {
      opacity: 1;
    }
  `)}
`;

const PlayerArea = styled.div`
  grid-column: 1;
  grid-row: 1;
  display: flex;
  position: relative;
`;

const DescriptionAndAnalytics = styled.div`
  grid-column: 2;
  grid-row: 1 / -1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  color: var(--text-secondary);
  align-self: start;
  max-height: 280px;
  overflow: hidden;

  @media only screen and (max-width: 900px) {
    grid-column: 1;
    grid-row: 2;
    align-self: stretch;
    overflow-y: auto;
  }

  &,
  *,
  *:before,
  *:after {
    box-sizing: border-box;
  }
`;

const TopicsInline = styled.div`
  margin-top: var(--spacing-md);

  h3 {
    margin: 0 0 8px 0;
    font-size: 14px;
    font-weight: 700;
    color: var(--foreground);
  }

  @media only screen and (max-width: 900px) {
    display: none;
  }
`;

const TopicsBelow = styled.div`
  grid-column: 1;
  grid-row: 3;
  display: none;

  h3 {
    margin: 0 0 8px 0;
    font-size: 14px;
    font-weight: 700;
    color: var(--foreground);
  }

  @media only screen and (max-width: 900px) {
    display: block;
  }
`;

const AccordionSection = styled.details<{ showDivider?: boolean }>`
  border-top: ${props => (props.showDivider ? '1px solid #484848' : 'none')};
  margin-top: ${props => (props.showDivider ? '10px' : '0')};
  padding-top: ${props => (props.showDivider ? '10px' : '0')};

  &:not(:first-of-type) {
    border-top: 1px solid #484848;
    margin-top: 10px;
    padding-top: 10px;
  }

  /* Always open, hide toggle */
  > summary {
    pointer-events: none;
    list-style: none;
    &::-webkit-details-marker {
      display: none;
    }
    &::after {
      display: none;
    }
  }
`;

const AccordionSummary = styled.summary`
  display: flex;
  align-items: center;
  cursor: pointer;
  list-style: none;
  gap: 16px;

  &::-webkit-details-marker {
    display: none;
  }

  h3 {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    color: var(--foreground);
  }

  &::after {
    content: '';
    border: solid #f3e9d7;
    border-width: 0 2px 2px 0;
    display: inline-block;
    padding: 3px;
    transform: rotate(45deg);
    margin-left: auto;
    flex-shrink: 0;
    transition: transform 0.2s;
  }

  details[open] > &::after {
    transform: rotate(-135deg);
  }

  /* Wide: hide chevron */
  @media only screen and (min-width: 901px) {
    cursor: default;
    &::after {
      display: none;
    }
  }

  .accordion-actions {
    display: flex;
    align-items: center;
    height: fit-content;
    flex-shrink: 0;
    gap: 8px;
    button {
      color: transparent;
      background-color: transparent;
      cursor: pointer;
      border: 0;
      outline: 0;
      &:active {
        svg {
          fill: white;
        }
      }
    }
  }
`;

const Description = styled.div<{ isSaving: boolean }>`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  h3 {
    margin-bottom: 5px;
    margin-top: 0;
    font-size: 18px;
    font-weight: var(--font-weight-bold);
  }

  .description-edit-content {
    padding-top: 8px;
    > textarea {
      display: block;
      width: 100%;
      color: var(--text-secondary);
      font-family: var(--font-primary);
      font-size: var(--text-sm);
      font-weight: var(--font-weight-regular);
      line-height: 1.5;
      background: transparent;
      outline: none;
      border: 1px solid var(--border);
      border-radius: 8px;
      resize: none;
      padding: 12px;
      min-height: 100px;
      max-height: 120px;
      opacity: 1;
      transition: var(--transition-normal);
      &::placeholder {
        color: var(--text-muted);
      }
      ${({ isSaving }) =>
        isSaving &&
        css`
          cursor: wait;
          opacity: 0.6;
        `}
    }
  }

  .description-content {
    font-size: var(--text-sm);
    font-weight: var(--font-weight-regular);
    line-height: 19.6px;
    color: var(--text-secondary);
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;

    padding-top: 8px;
    margin: 0;

    @media only screen and (max-width: 900px) {
      -webkit-line-clamp: 22;
    }

    > span {
      color: var(--text-muted);
      font-style: italic;
    }
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  line-height: var(--leading-text-xs);
  white-space: nowrap;
  background-color: var(--gray-600);
  color: var(--badge-blue-text);
`;

const DescriptionReadView = styled.div`
  position: relative;
`;

const SeeMoreButton = styled.button`
  position: absolute;
  bottom: 0;
  right: 0;
  background: linear-gradient(to right, transparent, var(--background) 30%);
  border: none;
  color: var(--primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  padding: 0 0 0 24px;
  cursor: pointer;
  line-height: 19.6px;
  transition: opacity var(--transition-fast);

  &:hover {
    opacity: 0.7;
  }
`;

const DescriptionModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding-right: 32px;

  h3 {
    margin: 0;
    font-size: var(--text-md);
    font-weight: var(--font-weight-bold);
    color: var(--foreground);
  }
`;

const DescriptionModalTextarea = styled.textarea`
  display: block;
  box-sizing: border-box;
  width: 100%;
  min-height: 200px;
  max-height: 60vh;
  margin: 0;
  padding: 12px;
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-normal);
  line-height: 1.6;
  color: var(--foreground);
  background: transparent;
  border: 1px solid var(--border);
  border-radius: 8px;
  resize: vertical;
  outline: none;

  &:focus {
    border-color: var(--accent);
  }

  &::placeholder {
    color: var(--text-muted);
  }

  &:disabled {
    cursor: wait;
    opacity: 0.6;
  }
`;

const DescriptionModalActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
`;

const DescriptionModalButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
`;

const ModalCancelButton = styled.button`
  padding: 8px 18px;
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semi-bold);
  color: var(--text-secondary);
  background: transparent;
  border: 1px solid var(--border);
  border-radius: 8px;
  cursor: pointer;
  transition: all var(--transition-fast);

  &:hover {
    background: var(--surface-elevated);
    border-color: var(--text-muted);
  }
`;

const ModalSaveButton = styled.button`
  padding: 8px 18px;
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semi-bold);
  color: var(--primary-foreground);
  background: var(--primary);
  border: 1px solid var(--primary);
  border-radius: 8px;
  cursor: pointer;
  transition: opacity var(--transition-fast);

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ProcessingPlayerPlaceholder = styled.div`
  position: relative;
  background: #000;
`;

const LowResStatusText = styled.div`
  position: absolute;
  width: fit-content;
  top: -32px;
  right: 0;
`;
